import { Component, computed, inject, OnDestroy, OnInit, Renderer2, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter, Observable, Subscription } from 'rxjs';
import { AppTopbar } from './app.topbar';
import { AppSidebar } from './app.sidebar';
import { LayoutService } from '../service/layout.service';
import { AppConfigurator } from './app.configurator';
import { BreadcrumbService2 } from '../../../../services/breadcrumb2.service';
import { ModalModule } from '../../../../components/modal/modal.module';
import { SpinnerModule } from '../../../../components/spinner/spinner.module';
import { MenuItem } from 'primeng/api';
import { BreadcrumbModule } from "primeng/breadcrumb";
import { NoticeCenterComponent } from "../../../../components/notice-center/notice-center.component";

@Component({
    selector: 'app-layout',
    standalone: true,
    imports: [CommonModule, AppTopbar, AppSidebar, RouterModule, BreadcrumbModule, SpinnerModule, ModalModule, NoticeCenterComponent],
    styleUrls: ["./app.layout.component.scss"],
    template: `<div class="layout-container" [ngClass]="containerClass()">
        <div app-topbar (toggleNotificationSidebar)="noticeCenter.toggleVisibility($event)"></div>
        <div app-sidebar></div>
        <div class="layout-content-wrapper">
            <div class="layout-content">
                @if (!hideBreadcrumb()) {
                    <p-breadcrumb class="d-none d-sm-block p-breadcrumb" [model]="items()" />
                }
                <router-outlet />
            </div>
        </div>
        <div class="layout-mask animate-fadein"></div>
    </div> 
    <modal />
    <app-notice-center #noticeCenter />
<spinner />`
    
})
export class AppLayout implements OnDestroy, OnInit {
    overlayMenuOpenSubscription: Subscription;

    menuOutsideClickListener: any;

    menuScrollListener: any;

    readonly appSidebar = viewChild.required(AppSidebar);

    readonly appTopbar = viewChild.required(AppTopbar);

    private breadcrumbService2 = inject(BreadcrumbService2);

    items = computed(() => this.breadcrumbService2.$crumbs());

    hideBreadcrumb = computed(() =>
        {
            console.log('hideBreadcrumb', this.breadcrumbService2.$hideBreadcrumb());
            return this.breadcrumbService2.$hideBreadcrumb()
        } );

    constructor(
        public layoutService: LayoutService,
        public renderer: Renderer2,
        public router: Router
    ) {
        this.overlayMenuOpenSubscription = this.layoutService.overlayOpen$.subscribe(() => {
            if (!this.menuOutsideClickListener) {
                this.menuOutsideClickListener = this.renderer.listen('document', 'click', (event) => {
                    const appSidebar = this.appSidebar();
                    const appMenu = this.appSidebar().appMenu();
                    const appTopbar = this.appTopbar();
                    const menuButton = this.appTopbar().menuButton();
                    const isOutsideClicked = !(
                        appMenu.el.nativeElement.isSameNode(event.target) ||
                        appMenu.el.nativeElement.contains(event.target) ||
                        menuButton.nativeElement.isSameNode(event.target) ||
                        menuButton.nativeElement.contains(event.target) ||
                        appSidebar.el.nativeElement.isSameNode(event.target) ||
                        appSidebar.el.nativeElement.contains(event.target)
                    );

                    if (isOutsideClicked) {
                        this.hideMenu();
                    }
                });
            }

            if ((this.layoutService.isSlim() || this.layoutService.isSlimPlus()) && !this.menuScrollListener) {
                this.menuScrollListener = this.renderer.listen(this.appSidebar().appMenu().menuContainer().nativeElement, 'scroll', (event) => {
                    if (this.layoutService.isDesktop()) {
                        this.hideMenu();
                    }
                });
            }

            if (this.layoutService.layoutState().staticMenuMobileActive) {
                this.blockBodyScroll();
            }
        });

        this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
            this.hideMenu();
        });
    }

    ngOnInit(): void {
    }

    hideMenu() {
        this.layoutService.layoutState.update((prev) => ({ ...prev, overlayMenuActive: false, staticMenuMobileActive: false, menuHoverActive: false }));
        this.layoutService.reset();
        if (this.menuOutsideClickListener) {
            this.menuOutsideClickListener();
            this.menuOutsideClickListener = null;
        }

        this.unblockBodyScroll();
    }

    blockBodyScroll(): void {
        if (document.body.classList) {
            document.body.classList.add('blocked-scroll');
        } else {
            document.body.className += ' blocked-scroll';
        }
    }

    unblockBodyScroll(): void {
        if (document.body.classList) {
            document.body.classList.remove('blocked-scroll');
        } else {
            document.body.className = document.body.className.replace(new RegExp('(^|\\b)' + 'blocked-scroll'.split(' ').join('|') + '(\\b|$)', 'gi'), ' ');
        }
    }

    containerClass = computed(() => {
        const layoutConfig = this.layoutService.layoutConfig();
        const layoutState = this.layoutService.layoutState();

        return {
            'layout-container': true,
            ['layout-topbar-' + layoutConfig.topbarTheme]: true,
            ['layout-menu-' + layoutConfig.menuTheme]: true,
            ['layout-menu-profile-' + layoutConfig.menuProfilePosition]: true,
            'layout-overlay': layoutConfig.menuMode === 'overlay',
            'layout-static': layoutConfig.menuMode === 'static',
            'layout-slim': layoutConfig.menuMode === 'slim',
            'layout-slim-plus': layoutConfig.menuMode === 'slim-plus',
            'layout-horizontal': layoutConfig.menuMode === 'horizontal',
            'layout-reveal': layoutConfig.menuMode === 'reveal',
            'layout-drawer': layoutConfig.menuMode === 'drawer',
            'layout-sidebar-dark': layoutConfig.colorScheme === 'dark',
            'layout-static-inactive': layoutState.staticMenuDesktopInactive && layoutConfig.menuMode === 'static',
            'layout-overlay-active': layoutState.overlayMenuActive,
            'layout-mobile-active': layoutState.staticMenuMobileActive,
            'layout-menu-profile-active': layoutState.rightMenuActive,
            'layout-sidebar-active': layoutState.sidebarActive,
            'layout-sidebar-anchored': layoutState.anchored
        };
    });

    ngOnDestroy() {
        if (this.overlayMenuOpenSubscription) {
            this.overlayMenuOpenSubscription.unsubscribe();
        }

        if (this.menuOutsideClickListener) {
            this.menuOutsideClickListener();
        }
    }
}
